import type { TemplateBlockInstance } from '@/templates/domain/types';
import type { SliceInfo } from '../domain/RendererContract';

export interface PageLayout {
  pages: Array<{
    items: Array<{
      instanceId: string;
      slice?: SliceInfo;
    }>;
  }>;
}

export const paginate = (input: {
  instances: TemplateBlockInstance[];
  heights: Map<string, number>;
  usableHeightPx: number;
  splittableBlocksRowCounts: Map<string, number>;
  /** Instance IDs whose renderer sets `pageBreakAfter` (e.g. cover_page). */
  pageBreakAfterIds?: ReadonlySet<string>;
  /** Instance IDs that must start a new page (a free-text block flagged `pageBreakBefore`). */
  pageBreakBeforeIds?: ReadonlySet<string>;
}): PageLayout => {
  const { instances, heights, usableHeightPx, splittableBlocksRowCounts, pageBreakAfterIds, pageBreakBeforeIds } = input;

  const pages: PageLayout['pages'] = [];
  let currentPageItems: PageLayout['pages'][number]['items'] = [];
  let usedPx = 0;

  const flushPage = () => {
    if (currentPageItems.length === 0) return;
    pages.push({ items: currentPageItems });
    currentPageItems = [];
    usedPx = 0;
  };

  const forceBreakAfter = (instanceId: string) => {
    if (pageBreakAfterIds?.has(instanceId)) {
      flushPage();
    }
  };

  const getHeight = (instanceId: string) => heights.get(instanceId) ?? 0;
  const getRowCount = (instanceId: string) => splittableBlocksRowCounts.get(instanceId);

  for (const instance of instances) {
    const instanceId = instance.templateBlockInstanceId;
    if (pageBreakBeforeIds?.has(instanceId) && currentPageItems.length > 0) {
      flushPage();
    }
    const fullHeight = getHeight(instanceId);
    const rowCount = getRowCount(instanceId);
    const isSplittableBlock = typeof rowCount === 'number' && rowCount > 0;

    if (!isSplittableBlock) {
      if (usedPx !== 0 && usedPx + fullHeight > usableHeightPx) {
        flushPage();
      }
      currentPageItems.push({ instanceId });
      usedPx += fullHeight;
      forceBreakAfter(instanceId);
      continue;
    }

    // If it fits, render the full block (no slice provided).
    if (usedPx + fullHeight <= usableHeightPx) {
      currentPageItems.push({ instanceId });
      usedPx += fullHeight;
      forceBreakAfter(instanceId);
      continue;
    }

    const rowHeight = fullHeight / rowCount;
    let startIndex = 0;

    while (startIndex < rowCount) {
      const remainingPx = usableHeightPx - usedPx;
      if (remainingPx <= 0) {
        flushPage();
        // Start the next page.
        continue;
      }

      // Approximation: row-height based on measured block height.
      let rowsFit = Math.floor(remainingPx / rowHeight);
      if (rowsFit <= 0) {
        if (currentPageItems.length > 0) {
          flushPage();
          continue;
        }
        // A row taller than a whole empty page (a page-sized picture in a prose block) can never fit: flushing an empty page
        // changes nothing, so the loop would spin forever. Place it anyway; it overflows its page instead of hanging the tab.
        rowsFit = 1;
      }

      rowsFit = Math.min(rowsFit, rowCount - startIndex);
      const endIndex = startIndex + rowsFit;

      const isContinuation = startIndex > 0;
      currentPageItems.push({
        instanceId,
        slice: {
          startIndex,
          endIndex,
          isContinuation
        }
      });

      usedPx += rowsFit * rowHeight;
      startIndex = endIndex;
    }

    forceBreakAfter(instanceId);
  }

  flushPage();

  // If everything was empty, preserve an empty structure.
  return { pages };
};

/** Structural equality so a repeat paginate does not replace page state (and remount fields). */
export function pageLayoutsEqual(left: PageLayout | null, right: PageLayout): boolean {
  if (!left || left.pages.length !== right.pages.length) return false;

  for (let pageIndex = 0; pageIndex < left.pages.length; pageIndex += 1) {
    const leftItems = left.pages[pageIndex]?.items ?? [];
    const rightItems = right.pages[pageIndex]?.items ?? [];
    if (leftItems.length !== rightItems.length) return false;

    for (let itemIndex = 0; itemIndex < leftItems.length; itemIndex += 1) {
      const leftItem = leftItems[itemIndex];
      const rightItem = rightItems[itemIndex];
      if (!leftItem || !rightItem || leftItem.instanceId !== rightItem.instanceId) return false;

      const leftSlice = leftItem.slice;
      const rightSlice = rightItem.slice;
      if (!leftSlice && !rightSlice) continue;
      if (!leftSlice || !rightSlice) return false;
      if (
        leftSlice.startIndex !== rightSlice.startIndex ||
        leftSlice.endIndex !== rightSlice.endIndex ||
        leftSlice.isContinuation !== rightSlice.isContinuation
      ) {
        return false;
      }
    }
  }

  return true;
}
